/**
 * Validation summary panel rendered in the RecordDetail view (TASK-159 / AV-01).
 *
 * Shows the cached validation result for a record — "X chyb, Y varování,
 * naposledy zkontrolováno HH:MM" — plus a "Zkontrolovat znovu" button that
 * issues a fresh ad-hoc validation. Phase 1 keeps the button always visible
 * (per plan CP-2); the ruleset-version-based gating is a follow-up.
 *
 * The panel is a controlled display of {@link ValidationStatus} cached on the
 * record (status / counts / validatedAt). When the recheck completes, the
 * fresh {@link ValidationResult} is rendered inline below the cached row so
 * the cataloger can compare without losing context. Persisting the fresh
 * result is the backend's job (the panel doesn't write — it just previews).
 */
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Accordion, Button } from '@folio/stripes/components';
import { useValidateRecord } from '../../hooks/useValidateRecord';
import { ValidationStatusBadge } from './ValidationStatusBadge';
import { ValidationIssueList } from './ValidationIssueList';
var formatIsoToHHMM = function (iso, locale) {
    if (!iso)
        return null;
    try {
        var d = new Date(iso);
        if (Number.isNaN(d.getTime()))
            return null;
        return d.toLocaleString(locale, {
            hour: '2-digit',
            minute: '2-digit',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    }
    catch (_a) {
        return null;
    }
};
export var ValidationPanel = function (_a) {
    var validationStatus = _a.validationStatus, errorCount = _a.errorCount, warningCount = _a.warningCount, validatedAt = _a.validatedAt, currentRecord = _a.currentRecord, recordType = _a.recordType, _b = _a.operation, operation = _b === void 0 ? 'PUBLISH' : _b, _c = _a.cachedIssues, cachedIssues = _c === void 0 ? [] : _c;
    var intl = useIntl();
    var _d = useState(null), freshResult = _d[0], setFreshResult = _d[1];
    var validate = useValidateRecord({
        onSuccess: function (result) { return setFreshResult(result); },
    });
    var displayResult = freshResult !== null && freshResult !== void 0 ? freshResult : {
        valid: validationStatus === 'VALID',
        errorCount: errorCount,
        warningCount: warningCount,
        errors: cachedIssues.filter(function (i) { return i.severity === 'BLOCKING'; }),
        warnings: cachedIssues.filter(function (i) { return i.severity === 'WARNING'; }),
    };
    var effectiveStatus = (function () {
        if (!freshResult)
            return validationStatus;
        if (freshResult.errorCount > 0)
            return 'HAS_ERRORS';
        if (freshResult.warningCount > 0)
            return 'HAS_WARNINGS_ONLY';
        return 'VALID';
    })();
    var handleRecheck = function () {
        if (!currentRecord || !recordType)
            return;
        validate.mutate({
            record: currentRecord,
            recordType: recordType,
            operation: operation,
            // CZNA-1619 — `recordToMarc` carries every stored link (`sourceAuthorityId`), so the
            // recheck reports an unlinked 368/372/374 value like the stored verdict does.
            carriesLinks: true,
        });
    };
    var validatedAtText = formatIsoToHHMM(validatedAt, intl.locale);
    var canRecheck = !!currentRecord && !!recordType;
    var accordionLabel = (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: '8px' } },
        React.createElement(ValidationStatusBadge, { status: effectiveStatus }),
        React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.panel.title" })));
    return (React.createElement(Accordion, { label: accordionLabel, "data-test-validation-panel": true },
        React.createElement("div", { "data-test-validation-panel-body": true },
            React.createElement("div", { style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                    flexWrap: 'wrap',
                } },
                React.createElement("div", { "data-test-validation-panel-summary": true },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.panel.summary", values: {
                            errors: displayResult.errorCount,
                            warnings: displayResult.warningCount,
                        } }),
                    validatedAtText && (React.createElement("span", { style: { color: '#666', marginLeft: '8px' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.panel.lastValidated", values: { when: validatedAtText } }))),
                    !validatedAtText && (React.createElement("span", { style: { color: '#888', marginLeft: '8px' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.panel.neverValidated" })))),
                canRecheck && (React.createElement(Button, { onClick: handleRecheck, disabled: validate.isLoading, "data-test-validation-recheck-button": true },
                    React.createElement(FormattedMessage, { id: validate.isLoading
                            ? 'ui-czna-authorities.validation.button.rechecking'
                            : 'ui-czna-authorities.validation.button.recheck' })))),
            validate.isError && (React.createElement("p", { "data-test-validation-recheck-error": true, style: { color: '#c0392b', marginTop: '8px' } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.panel.recheckError" }))),
            (displayResult.errorCount > 0 || displayResult.warningCount > 0) && (React.createElement("div", { style: { marginTop: '12px' } },
                displayResult.errorCount > 0 && (React.createElement(React.Fragment, null,
                    React.createElement("h4", { style: { margin: '8px 0 4px' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.panel.errorsHeading", values: { count: displayResult.errorCount } })),
                    React.createElement(ValidationIssueList, { issues: displayResult.errors }))),
                displayResult.warningCount > 0 && (React.createElement(React.Fragment, null,
                    React.createElement("h4", { style: { margin: '8px 0 4px' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.panel.warningsHeading", values: { count: displayResult.warningCount } })),
                    React.createElement(ValidationIssueList, { issues: displayResult.warnings }))))))));
};
export default ValidationPanel;
